import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu as MenuIcon, X } from "lucide-react";
import { BOOKING_URL, NAV_LINKS, scrollToId, trackEvent } from "@/data/content";

export const HornsMark = ({ className = "w-7 h-5 text-[#C5A059]" }) => (
  <svg viewBox="0 0 44 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M3 22 C1 10 8 2 20 1 C11 7 9 14 11 22 Z" />
    <path d="M41 22 C43 10 36 2 24 1 C33 7 35 14 33 22 Z" />
  </svg>
);

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    setTimeout(() => scrollToId(href), open ? 250 : 0);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "bg-[#0D0C0B]/85 backdrop-blur-xl border-b border-[#C5A059]/15"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[70px] flex items-center justify-between">
          <button
            data-testid="header-logo"
            onClick={() => window.__lenis?.scrollTo(0)}
            className="flex items-center group"
          >
            <img
              src="/img/logo.png"
              alt="John Tartarino — Ristorante di carne e cucina maremmana, Follonica"
              className="h-11 w-auto transition-transform duration-500 group-hover:scale-105"
            />
          </button>

          <nav className="hidden lg:flex items-center gap-7" data-testid="header-nav">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => go(l.href)}
                className="text-[13px] tracking-wide text-[#C5BCAD] hover:text-[#C5A059] transition-colors duration-300"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="header-prenota-cta"
              onClick={() => go("#prenota")}
              className="hidden sm:inline-flex items-center px-5 py-2.5 text-[13px] tracking-[0.15em] uppercase border border-[#C5A059]/60 text-[#C5A059] hover:bg-[#C5A059] hover:text-[#0D0C0B] transition-colors duration-300"
            >
              Prenota
            </button>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(!open)}
              className="lg:hidden text-[#F7F4EE] p-2"
              aria-label="Menu"
            >
              {open ? <X size={22} /> : <MenuIcon size={22} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="mobile-nav-overlay"
            className="fixed inset-0 z-40 bg-[#0D0C0B]/97 backdrop-blur-2xl flex flex-col items-center justify-center gap-6 lg:hidden"
          >
            {NAV_LINKS.map((l, i) => (
              <motion.button
                key={l.href}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.06 * i, duration: 0.5 }}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => go(l.href)}
                className="font-display text-2xl text-[#F7F4EE] hover:text-[#C5A059] transition-colors"
              >
                {l.label}
              </motion.button>
            ))}
            <motion.button
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              data-testid="mobile-nav-prenota"
              onClick={() => go("#prenota")}
              className="mt-4 px-8 py-3 border border-[#C5A059] text-[#C5A059] uppercase tracking-[0.2em] text-sm"
            >
              Prenota
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
